<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import businessIcon from '@/assets/icons/business.svg';
import personIcon from '@/assets/icons/person.svg';
import { useBankTransfer } from '@/composables/useBankTransfer';
import { useErrorModal } from '@/composables/useErrorModal';
import { useGatedRef } from '@/composables/useGatedRef';
import { useTips } from '@/composables/useTips';
import { useViewBreakpoints } from '@/composables/useViewBreakpoints';
import {
  type BankTransferRecipient,
  BankTransferRecipientType
} from '@/references/axios/bankTransfer/types';
import { useI18n } from '@/references/i18n';
import { RouteNames } from '@/router';

import type { BeneficiarySelectorItemInfo } from '@/components/BeneficiarySelectorItem.types';
import BeneficiarySelectorItem from '@/components/BeneficiarySelectorItem.vue';
import ListSkeleton from '@/components/ListSkeleton.vue';
import type { UiDropdownItem } from '@/components/ui/UiDropdown.types';
import UiDropdown from '@/components/ui/UiDropdown.vue';
import UiPlusButton from '@/components/ui/UiPlusButton.vue';
import UiText from '@/components/ui/UiText.vue';
import UiTip from '@/components/ui/UiTip.vue';

const emit = defineEmits<{
  (event: 'select', value: number): void;
}>();

const otcTipComponent = ref<typeof UiTip>();

const { recipients, isLoaded, loadRecipients, removeRecipient } = useBankTransfer();
const { t } = useI18n();
const { push } = useRouter();
const toast = useErrorModal();
const { closeOTCTip, isOTCTipClosed } = useTips();
const { isMobile } = useViewBreakpoints();
const isGatedLoaded = useGatedRef(isLoaded);

const newOptions = computed((): UiDropdownItem[] => [
  {
    text: t('home.beneficiaries.create.types.individual'),
    icon: personIcon,
    handler() {
      closeOTCTip();
      push({
        name: RouteNames.CreateBeneficiary,
        params: { type: BankTransferRecipientType.Individual }
      });
    }
  },
  {
    text: t('home.beneficiaries.create.types.business'),
    icon: businessIcon,
    handler() {
      closeOTCTip();
      push({
        name: RouteNames.CreateBeneficiary,
        params: { type: BankTransferRecipientType.Business }
      });
    }
  }
]);

const processedRecipients = computed((): BeneficiarySelectorItemInfo[] => {
  return recipients.value.map((item) => ({
    id: item.id,
    color: '',
    letters: '',
    title: formatRecipient(item),
    desciprtion: '',
    search: `${item.firstName} ${item.lastName} ${item.nickname}`,
    amount: item.amount
  }));
});

function select(id: number): void {
  otcTipComponent.value?.hide();
  emit('select', id);
}

async function remove(id: number): Promise<void> {
  await removeRecipient(id);
  toast.auto(t('toasts.beneficiaryRemoved'));
}

function formatRecipient(recipient: BankTransferRecipient): string {
  if (recipient.nickname) {
    return recipient.nickname;
  }

  if (recipient.type === BankTransferRecipientType.Business) {
    return recipient.firstName.toUpperCase();
  }

  return `${recipient.firstName} ${recipient.lastName}`.toUpperCase();
}

function onOTCTipHide(): void {
  closeOTCTip();
}

onMounted(() => {
  if (!isOTCTipClosed.value) {
    otcTipComponent.value?.show();
  }
});

loadRecipients();
</script>

<template>
  <div class="beneficiary-selector">
    <UiText class="beneficiary-selector__label" tag="p" size="md" weight="regular" muted>
      {{ t('home.beneficiaries.title') }}
    </UiText>
    <div class="beneficiary-selector__dropdown-wrapper">
      <div class="beneficiary-selector__otc-tip-wrapper">
        <UiTip
          ref="otcTipComponent"
          :title="t('tooltips.otc.title')"
          :description="t('tooltips.otc.description')"
          :position="isMobile ? 'bottom-start' : 'left'"
          :distance="52"
          custom-class="beneficiary-selector__otc-tip"
          @hide="onOTCTipHide"
        />
      </div>
      <UiDropdown
        class="beneficiary-selector__dropdown"
        position="bottom-start"
        :items="newOptions"
        muted-icons
      >
        <UiPlusButton
          :title="t('home.beneficiaries.create.title')"
          :description="t('home.beneficiaries.create.description')"
          :underlay="false"
        />
      </UiDropdown>
    </div>

    <ListSkeleton v-if="!isGatedLoaded" :underlay="false" />
    <template v-else-if="processedRecipients.length">
      <UiText class="beneficiary-selector__label" muted tag="p">
        {{ t('home.beneficiaries.recent.groupTitle') }}
      </UiText>
      <ul class="beneficiary-selector__list">
        <li
          v-for="recipient in processedRecipients"
          :key="recipient.id"
          class="beneficiary-selector__item"
        >
          <BeneficiarySelectorItem
            :info="recipient"
            @select="select(recipient.id)"
            @remove="remove(recipient.id)"
          />
        </li>
      </ul>
    </template>
  </div>
</template>

<style scoped>
.beneficiary-selector {
  padding: 16px;
  background-color: var(--color-main-underlay);
  border-radius: 12px;
}

.beneficiary-selector__label {
  margin: 0 0 24px;
}

.beneficiary-selector__dropdown-wrapper {
  position: relative;
}

.beneficiary-selector__dropdown-wrapper:not(:last-child) {
  margin: 0 0 24px;
}

.beneficiary-selector__otc-tip-wrapper {
  position: absolute;
  bottom: 20px;
  left: 20px;
  pointer-events: none;
}

.beneficiary-selector__dropdown {
  width: 100%;
}

.beneficiary-selector__list {
  margin: 0;
  padding: 0;
  list-style-type: none;
}

.beneficiary-selector__item:not(:last-child) {
  margin: 0 0 24px;
}
</style>

<style>
.beneficiary-selector__otc-tip .v-popper__inner {
  max-width: 212px;
}
</style>
